import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ACQUISITION_LABELS,
  CONFIDENCE_CHIP,
  CONFIDENCE_LABELS,
  PHOTO_KIND_LABELS,
  api,
  formatDate,
  mineralTitle,
  photoUrl,
  type Specimen,
} from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { Gallery } from '../components/Gallery.tsx';
import { PhotoUploader } from '../components/PhotoUploader.tsx';
import { MoneyBlock } from '../components/MoneyBlock.tsx';
import { CoordHint } from '../components/CoordHint.tsx';
import { Provenance } from '../components/Provenance.tsx';
import { Identify } from '../components/Identify.tsx';

export function SpecimenScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [specimen, setSpecimen] = useState<Specimen | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addingPhoto, setAddingPhoto] = useState(false);

  const reload = useCallback(() => {
    if (!id) return;
    api.specimen(id).then(setSpecimen).catch((e: Error) => setError(e.message));
  }, [id]);

  useEffect(reload, [reload]);

  if (error) return <Message text={error} />;
  if (!specimen) return <Message text="Загрузка…" />;

  // Этикетка продавца — документ, а не иллюстрация: в общую галерею она
  // не идёт, у неё свой блок с подписью (ТЗ 4.4).
  const galleryPhotos = specimen.photos.filter((p) => p.kind !== 'label');
  const labelPhotos = specimen.photos.filter((p) => p.kind === 'label');

  const primary = specimen.minerals.find((m) => m.is_primary);
  const others = specimen.minerals.filter((m) => !m.is_primary);
  const size = [specimen.size_x_mm, specimen.size_y_mm, specimen.size_z_mm];
  const hasSize = size.some(Boolean);

  async function remove() {
    if (!id) return;
    await api.deleteSpecimen(id);
    navigate('/');
  }

  return (
    <div className="app">
      <div className="topbar">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Назад">
          <Icon name="back" className="icon muted" />
        </button>
        <div className="spacer" />
        <Link className="icon-btn" to={`/specimen/${specimen.id}/edit`} aria-label="Редактировать">
          <Icon name="edit" className="icon muted" />
        </Link>
      </div>

      <div className="scroll">
        <Gallery photos={galleryPhotos} onChanged={reload} />

        {addingPhoto ? (
          <PhotoUploader
            specimenId={specimen.id}
            onUploaded={() => {
              setAddingPhoto(false);
              reload();
            }}
          />
        ) : (
          <button
            type="button"
            className="btn btn--full"
            style={{ marginTop: 'var(--space-3)' }}
            onClick={() => setAddingPhoto(true)}
          >
            <Icon name="camera" className="icon icon-sm" /> Добавить снимок
          </button>
        )}

        <div className="inv-line">{specimen.inv_number}</div>
        <h1 className="specimen-name">
          {primary ? mineralTitle(primary) : <span className="muted">без названия</span>}
        </h1>
        {primary && (
          <div className="small muted">
            {[
              primary.name_ru ? primary.name : null,
              primary.formula,
              primary.hardness_min
                ? `твёрдость ${[Number(primary.hardness_min), Number(primary.hardness_max)]
                    .filter((v) => Number.isFinite(v))
                    .filter((v, i, all) => i === 0 || v !== all[0])
                    .join('–')}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
        {others.some((m) => m.kind === 'variety') && (
          <div className="small muted">
            разновидность: {mineralTitle(others.find((m) => m.kind === 'variety')!).toLowerCase()}
          </div>
        )}

        {specimen.trade_name && (
          <div className="seller-words">
            <div className="label-key" style={{ marginTop: 0 }}>Со слов продавца</div>
            {specimen.trade_name}
          </div>
        )}

        {labelPhotos.length > 0 && (
          <div className="label-photos">
            <div className="label-key" style={{ marginTop: 0 }}>
              Этикетка продавца
            </div>
            <div className="strip">
              {labelPhotos.map((photo) => (
                <a key={photo.id} href={photoUrl(photo.id)} target="_blank" rel="noreferrer">
                  <img
                    className="label-thumb"
                    src={photoUrl(photo.id, true)}
                    alt={PHOTO_KIND_LABELS.label}
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="chips">
          {others.map((m) => (
            <span key={m.mineral_id} className="chip chip--plain" title={m.note ?? undefined}>
              {mineralTitle(m)}
            </span>
          ))}
          {primary && (
            <span className={CONFIDENCE_CHIP[primary.confidence]}>
              {CONFIDENCE_LABELS[primary.confidence]}
            </span>
          )}
          {primary && primary.mindat_id === null && (
            <Link
              className="chip chip--warn"
              to="/reference"
              title="Название занесено текстом — свяжите его со справочником"
            >
              не привязан к справочнику
            </Link>
          )}
        </div>

        {/* Координаты из снимков — предложение или разбор расхождений (ТЗ 7.3). */}
        <CoordHint specimen={specimen} onChanged={reload} />

        <dl className="rows">
          {specimen.locality_name && (
            <Row icon="pin" label="Месторождение">
              {[specimen.locality_name, specimen.locality_country].filter(Boolean).join(', ')}
              {specimen.locality_note && (
                <div className="hint">{specimen.locality_note}</div>
              )}
            </Row>
          )}
          <Row icon="pick" label="Поступление">
            {ACQUISITION_LABELS[specimen.acquisition_type]}
            {specimen.acquired_on ? ` · ${formatDate(specimen.acquired_on, false)}` : ''}
            {specimen.acquired_from && <div className="hint">{specimen.acquired_from}</div>}
          </Row>
          {hasSize && (
            <Row icon="ruler" label="Размер">
              {size.filter(Boolean).map((v) => Number(v)).join(' × ')} мм
            </Row>
          )}
          {specimen.weight_g && <Row icon="ruler" label="Вес">{Number(specimen.weight_g)} г</Row>}
          {specimen.effective_latitude && (
            <Row icon="pin" label="Координаты">
              <Link to={`/map?q=${encodeURIComponent(specimen.inv_number)}`} className="coord-link">
                {Number(specimen.effective_latitude).toFixed(5)},{' '}
                {Number(specimen.effective_longitude).toFixed(5)}
              </Link>
              <div className="hint">
                {[
                  specimen.effective_precision_m != null
                    ? `±${specimen.effective_precision_m} м`
                    : null,
                  COORD_SOURCE_LABELS[specimen.effective_coord_source ?? ''] ?? null,
                  specimen.inherited_from_trip ? 'от выезда' : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </Row>
          )}
          {specimen.storage_location && (
            <Row icon="tag" label="Хранение">{specimen.storage_location}</Row>
          )}
        </dl>

        {specimen.notes && (
          <>
            <div className="label-key">Заметки</div>
            <p className="notes">{specimen.notes}</p>
          </>
        )}

        {/* Версии о минерале по снимкам. Стоит после заметок и до цепочки
            владельцев: это вопрос о самом камне, а не о его прошлом. */}
        <Identify
          specimenId={specimen.id}
          hasPhotos={specimen.photos.length > 0}
          onAccepted={reload}
        />

        {/* История до вас — не то же самое, что «от кого куплено» (ТЗ 5.9). */}
        <Provenance specimenId={specimen.id} />

        {/* Деньги — отдельным блоком, потому что целиком исчезают в режиме
            «без цен» (ТЗ 4.4, пункт 7). */}
        <MoneyBlock specimen={specimen} onChanged={reload} />


        {/* Блок выезда: связывает образец с партией, привезённой в тот же день. */}
        {specimen.trip_id && (
          <Link className="trip-block" to={`/trips/${specimen.trip_id}`}>
            <Icon name="route" className="icon muted" />
            <div className="trip-block-text">
              Привезён {formatDate(specimen.trip_started_on)}
              {specimen.trip_title ? ` · ${specimen.trip_title}` : ''}
              <span className="hint">
                {specimen.trip_sibling_count > 0
                  ? `ещё ${specimen.trip_sibling_count} ${plural(specimen.trip_sibling_count)} из этой поездки`
                  : 'единственный образец из этой поездки'}
                {specimen.inherited_from_trip ? ' · место взято отсюда' : ''}
              </span>
            </div>
            <div className="spacer" />
            <Icon name="chevron" className="icon icon-sm muted" />
          </Link>
        )}

        <div className="actions">
          <Link className="btn" to={`/labels?ids=${specimen.id}`}>
            <Icon name="printer" className="icon icon-sm" /> Этикетка
          </Link>
        </div>

        <div className="actions" style={{ marginTop: 'var(--space-3)' }}>
          {confirmDelete ? (
            <>
              <button className="btn" onClick={() => setConfirmDelete(false)}>
                Отмена
              </button>
              <button className="btn btn--danger" onClick={remove}>
                Удалить в корзину
              </button>
            </>
          ) : (
            <button className="btn" onClick={() => setConfirmDelete(true)}>
              Удалить
            </button>
          )}
        </div>
        {confirmDelete && (
          <div className="hint centered">Удаление обратимо: образец пролежит в корзине 30 дней</div>
        )}
      </div>
    </div>
  );
}

/** Источник координат: точка из Mindat и точка из EXIF различаются на порядки (ТЗ 9.1). */
const COORD_SOURCE_LABELS: Record<string, string> = {
  manual: 'поставлено вручную',
  exif: 'из снимка',
  mindat: 'из справочника',
};

function plural(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'образец';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'образца';
  return 'образцов';
}

function Row({
  icon,
  label,
  children,
}: {
  icon: 'pin' | 'pick' | 'ruler' | 'tag';
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="row">
      <dt>
        <Icon name={icon} className="icon icon-sm" />
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}

function Message({ text }: { text: string }) {
  return (
    <div className="app">
      <div className="topbar">
        <Link className="icon-btn" to="/" aria-label="Назад">
          <Icon name="back" className="icon muted" />
        </Link>
      </div>
      <div className="scroll muted small">{text}</div>
    </div>
  );
}
